<template>
  <div>
    <v-row>
      <v-col cols="12" md="6" v-for="card in ['totalDocuments', 'totalStorage']" :key="card">
        <v-card>
          <v-card-title>{{ $t(card) }}</v-card-title>
          <v-card-text class="text-h4">
            <v-skeleton-loader v-if="loading" type="text" width="100px" />
            <template v-else>
              {{ card === 'totalDocuments' ? totalDocuments : formatStorage(totalStorage) }}
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="loading" type="image" height="400px" />
        <TeamUserDocumentBarChart v-else 
          :filterDocumentsContext="filterDocumentsContext"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="loading" type="image" height="400px" />
        <DocumentFileTypeBarChart v-else 
          :filterDocumentsContext="filterDocumentsContext"
        /> 
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="loading" type="image" height="400px" />
         <CountCreatedLineChart v-else 
          :filterDocumentsContext="filterDocumentsContext"
        />
      </v-col>
    </v-row>
     <v-row>
      <v-col cols="12" md="12">
        <v-card>
          <v-card-title class="pa-0">
            <v-container fluid>
              <v-row no-gutters align="center">
                <v-col cols="auto">
                  <v-tabs v-model="activeTab">
                    <v-tab value="all">{{ $t('documentRepository') }}</v-tab>
                    <v-tab value="latest">
                      {{ $t('latestDocuments') }}
                      <v-badge
                        :content="recentDocumentsCount"
                        :model-value="recentDocumentsCount > 0"
                        color="error"
                        inline
                        class="ml-2"
                      ></v-badge>
                    </v-tab>           
                  </v-tabs>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-text-field
                    v-if="activeTab === 'all'"
                    v-model="search"
                    hide-details
                    density="compact"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    style="width: 300px;"
                    class="mt-2 mb-2"
                  ></v-text-field>
                  <v-text-field
                    v-else
                    v-model="searchLatest"
                    hide-details
                    density="compact"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    style="width: 300px;"
                    class="mt-2 mb-2"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-title>
          <v-card-text>
            <v-window v-model="activeTab">
              <v-window-item value="latest">               
                <v-skeleton-loader v-if="loading" type="list-item-three-line" :types="{ skeleton: 'list-item' }" :boilerplate="true" :tile="true" :loading="true" />
                <template v-else>
                  <v-data-table
                    :headers="allDocumentsHeaders"
                    :items="filteredLatestDocuments"
                    :items-per-page="20"
                    class="elevation-1"
                    :locale="$i18n.locale"
                    :no-data-text="$t('noData')"
                    :items-per-page-text="$t('itemsPerPage')"
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td @click="handleItemClick(item)" style="cursor: pointer;">
                          <v-icon :icon="item.isFolder ? 'mdi-folder' : 'mdi-file-document'" class="mr-2"></v-icon>
                          {{ item.displayName }}
                        </td>
                        <td>{{ item.fileType }}</td>
                        <td>{{ formatStorage(item.fileSize) }}</td>
                        <td>
                          <v-tooltip :text="formatDate(item.createdDate, true)" location="bottom">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ formatTimeAgo(item.createdDate) }}</span>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>
                          <v-tooltip :text="item.fullPath" location="top">
                            <template v-slot:activator="{ props }">
                              <div v-bind="props" class="d-flex align-center">
                                <v-icon icon="mdi-folder" size="small" class="mr-2"></v-icon>
                                <span>{{ getParentFolderNameFromCache(item.parentFolderId) }}</span>
                              </div>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>{{ item.teamName || 'Unknown Team' }}</td>
                        <td>
                          <v-tooltip
                            :text="item.userDisplayName || 'Unknown User'"
                            location="bottom"
                          >
                            <template v-slot:activator="{ props }">
                              <v-avatar
                                v-bind="props"
                                :size="28"
                                class="mr-1"
                              >
                                <v-img
                                  v-if="item.userAvatarUrl"
                                  :src="'https://' + item.userAvatarUrl"
                                  alt="User Avatar"
                                ></v-img>
                                <span
                                  v-else
                                  class="d-flex justify-center align-center text-caption white--text"
                                  :style="{
                                    backgroundColor: getRandomColor(item.createdByUserId),
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%'
                                  }"
                                >
                                  {{ getInitials(getUserName(item.createdByUserId)) }}
                                </span>
                              </v-avatar>
                            </template>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-window-item>
              <v-window-item value="all">            
                <v-skeleton-loader v-if="loading" type="list-item-three-line" :types="{ skeleton: 'list-item' }" :boilerplate="true" :tile="true" :loading="true" />
                <template v-else>
                  <v-breadcrumbs :items="breadcrumbs">
                    <template v-slot:item="{ item }">
                      <v-breadcrumbs-item
                        :title="item.title"
                        :disabled="item.disabled"
                        :href="item.href"
                        @click.prevent="goToPath(item)"
                      >
                        {{ item.title }}
                      </v-breadcrumbs-item>
                    </template>
                  </v-breadcrumbs>

                  <v-data-table
                    :headers="headers"
                    :items="filteredDocuments"
                    :items-per-page="20"
                    class="elevation-1"
                    :locale="$i18n.locale"
                    :no-data-text="$t('noData')"
                    :items-per-page-text="$t('itemsPerPage')"
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td @click="handleItemClick(item)" style="cursor: pointer;">
                          <v-icon :icon="item.isFolder ? 'mdi-folder' : 'mdi-file-document'" class="mr-2"></v-icon>
                          {{ item.displayName }}
                        </td>
                        <td>{{ item.fileType }}</td>
                        <td>{{ formatStorage(item.fileSize) }}</td>
                        <td>
                          <v-tooltip :text="formatDate(item.createdDate, true)" location="bottom">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ formatDate(item.createdDate) }}</span>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>{{ item.teamName || 'Unknown Team' }}</td>
                        <td>
                          <v-tooltip
                            :text="item.userDisplayName || 'Unknown User'"
                            location="bottom"
                          >
                            <template v-slot:activator="{ props }">
                              <v-avatar
                                v-bind="props"
                                :size="28"
                                class="mr-1"
                              >
                                <v-img
                                  v-if="item.userAvatarUrl"
                                  :src="'https://' + item.userAvatarUrl"
                                  alt="User Avatar"
                                ></v-img>
                                <span
                                  v-else
                                  class="d-flex justify-center align-center text-caption white--text"
                                  :style="{
                                    backgroundColor: getRandomColor(item.createdByUserId),
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%'
                                  }"
                                >
                                  {{ getInitials(getUserName(item.createdByUserId)) }}
                                </span>
                              </v-avatar>
                            </template>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { format, formatDistanceToNow } from 'date-fns';
import DocumentFileTypeBarChart from './charts/DocumentFileTypeBarChart.vue';
import TeamUserDocumentBarChart from './charts/TeamUserDocumentBarChart.vue';
import CountCreatedLineChart from './charts/CountCreatedLineChart.vue';

const store = useStore();
const { t } = useI18n();
const dataLoaded = computed(() => store.getters.getDataLoaded);
const buildMode = computed(() => store.state.buildMode);

const filterDocumentsContext = computed(() => store.state.filterDocumentsContext);

// We'll use DuckDB instead of storing documents in memory
const currentFolderId = ref("");
const rootFolderId = ref("");
const loading = ref(true);
const search = ref('');
const searchLatest = ref('');
const activeTab = ref('all');

const allDocumentsHeaders = computed(() => [
      { title: t('name'), key: 'displayName' },
      { title: t('type'), key: 'fileType' },
      { title: t('size'), key: 'fileSize' },
      { title: t('date'), key: 'createdDate' },
      { title: t('folder'), key: 'parentFolderId' },
      { title: t('uploadedBy'), key: 'teamId' },
      { title: '', key: 'userId' }
    ]);

    const headers = computed(() => [
      { title: t('name'), key: 'displayName' },
      { title: t('type'), key: 'fileType' },
      { title: t('size'), key: 'fileSize' },
      { title: t('uploadedDate'), key: 'createdDate' },
      { title: t('uploadedBy'), key: 'teamId' },
      { title: '', key: 'userId' }
    ]);

const getData = async () => {
  try {
    loading.value = true;
    
    // Get the DuckDB connection from the store
    const conn = store.getters.getDBConnection;
    if (!conn) {
      console.error('DuckDB connection not available');
      return;
    }
    
    // Query for the root folder
    const rootFolderResult = await conn.query(`
      SELECT "Id" FROM filteredDocuments 
      WHERE "FullPath" = '/' AND "IsFolder" = TRUE
      LIMIT 1
    `);
    
    const rootFolderRows = rootFolderResult.toArray();
    if (rootFolderRows.length > 0) {
      const rootFolder = rootFolderRows[0];
      rootFolderId.value = rootFolder.Id;
      currentFolderId.value = rootFolder.Id;
    }

    await store.commit('resetDocumentsFilters');
    
  } catch (error) {
    console.error("Error getting root folder:", error);
  } finally {
    loading.value = false;
  }
};

const filteredLatestDocuments = ref([]);

const fetchLatestDocuments = async () => {
  try {
    const conn = store.getters.getDBConnection;
    if (!conn) return;
    
    let query = `
      SELECT d.*, 
        t."Name" as "TeamName", 
        u."DisplayName" as "UserDisplayName", 
        u."AvatarUrl" as "UserAvatarUrl"
      FROM filteredDocuments d
      LEFT JOIN teams t ON d."CreatedByTeamId" = t."Id"
      LEFT JOIN users u ON d."CreatedByUserId" = u."Id"
      WHERE d."IsFolder" = FALSE
      ORDER BY d."CreatedDate" DESC
    `;
    
    if (searchLatest.value) {
      const searchTerm = searchLatest.value.replace(/'/g, "''");
      query = `
        SELECT d.*, 
          t."Name" as "TeamName", 
          u."DisplayName" as "UserDisplayName", 
          u."AvatarUrl" as "UserAvatarUrl"
        FROM filteredDocuments d
        LEFT JOIN teams t ON d."CreatedByTeamId" = t."Id"
        LEFT JOIN users u ON d."CreatedByUserId" = u."Id"
        WHERE d."IsFolder" = FALSE
          AND (
            d."DisplayName" LIKE '%${searchTerm}%'
            OR d."FileType" LIKE '%${searchTerm}%'
            OR d."CreatedByUserId" LIKE '%${searchTerm}%'
            OR d."CreatedByTeamId" LIKE '%${searchTerm}%'
          )
        ORDER BY d."CreatedDate" DESC
      `;
    }
    
    const result = await conn.query(query);
    filteredLatestDocuments.value = result.toArray().map(row => {
      const json = {};
      for (const key in row) {
        // Convert DuckDB column names to camelCase for Vue component
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        json[camelKey] = row[key];
      }
      return json;
    });
    
  } catch (error) {
    console.error("Error fetching latest documents:", error);
    filteredLatestDocuments.value = [];
  }
};

const filteredDocuments = ref([]);

const fetchFilteredDocuments = async () => {
  try {
    const conn = store.getters.getDBConnection;
    if (!conn) return;
    
    let query;
    
    if (search.value) {
      const searchTerm = search.value.replace(/'/g, "''");
      query = `
        SELECT d.*, 
          t."Name" as "TeamName", 
          u."DisplayName" as "UserDisplayName", 
          u."AvatarUrl" as "UserAvatarUrl"
        FROM filteredDocuments d
        LEFT JOIN teams t ON d."CreatedByTeamId" = t."Id"
        LEFT JOIN users u ON d."CreatedByUserId" = u."Id"
        WHERE 
          d."DisplayName" LIKE '%${searchTerm}%'
          OR d."FileType" LIKE '%${searchTerm}%'
          OR d."CreatedByUserId" LIKE '%${searchTerm}%'
          OR d."CreatedByTeamId" LIKE '%${searchTerm}%'
      `;
    } else {
      if (currentFolderId.value === "" || !currentFolderId.value) {
        // Root folder - get items where parent folder's FullPath is '/'
        query = `
          WITH RootFolder AS (
            SELECT "Id" 
            FROM filteredDocuments 
            WHERE "FullPath" = '/' AND "IsFolder" = TRUE
          )
          SELECT d.*, 
            t."Name" as "TeamName", 
            u."DisplayName" as "UserDisplayName", 
            u."AvatarUrl" as "UserAvatarUrl"
          FROM filteredDocuments d
          LEFT JOIN teams t ON d."CreatedByTeamId" = t."Id"
          LEFT JOIN users u ON d."CreatedByUserId" = u."Id"
          WHERE d."ParentFolderId" IN (SELECT "Id" FROM RootFolder)
        `;
      } else {
        query = `
          SELECT d.*, 
            t."Name" as "TeamName", 
            u."DisplayName" as "UserDisplayName", 
            u."AvatarUrl" as "UserAvatarUrl"
          FROM documents d
          LEFT JOIN teams t ON d."CreatedByTeamId" = t."Id"
          LEFT JOIN users u ON d."CreatedByUserId" = u."Id"
          WHERE d."ParentFolderId" = '${currentFolderId.value}'
        `;
      }
    }
    
    const result = await conn.query(query);
    filteredDocuments.value = result.toArray().map(row => {
      const json = {};
      for (const key in row) {
        // Convert DuckDB column names to camelCase for Vue component
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        json[camelKey] = row[key];
      }
      return json;
    });
    
  } catch (error) {
    console.error("Error fetching filtered documents:", error);
    filteredDocuments.value = [];
  }
};

const recentDocumentsCount = ref(0);

const computeRecentDocumentsCount = async () => {
  try {
    const conn = store.getters.getDBConnection;
    if (!conn) return;
    
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    
    const query = `
      SELECT COUNT(*) AS RecentCount
      FROM filteredDocuments
      WHERE "IsFolder" = FALSE
        AND "CreatedDate" > '${twentyFourHoursAgo}'
    `;
    
    const result = await conn.query(query);
    const row = result.toArray()[0];
    
    if (!row) {
      recentDocumentsCount.value = 0;
      return;
    }
    
    recentDocumentsCount.value = Number(row.RecentCount);
    
  } catch (error) {
    console.error("Error computing recent documents count:", error);
    recentDocumentsCount.value = 0;
  }
};

// Watch for filter context changes
const filterContext = computed(() => store.state.filterDocumentsContext);

watch(
  () => filterContext.value?.filtersLastUpdated,
  async () => {
    if (dataLoaded.value) {
      await refreshData();
    }
  }
);

const totalDocuments = ref(0);
const totalStorage = ref(0);

const computeCardValues = async () => {
  try {
    const conn = store.getters.getDBConnection;
    if (!conn) return;
    
    const query = `
      SELECT 
        COUNT(*) AS TotalDocuments,
        SUM("FileSize") AS TotalStorage
      FROM filteredDocuments
      WHERE "IsFolder" = FALSE
    `;
    
    const result = await conn.query(query);
    const row = result.toArray()[0];
    
    if (!row) {
      totalDocuments.value = 0;
      totalStorage.value = 0;
      return;
    }
    
    totalDocuments.value = Number(row.TotalDocuments);
    totalStorage.value = Number(row.TotalStorage);
    
  } catch (error) {
    console.error("Error computing card values:", error);
    totalDocuments.value = 0;
    totalStorage.value = 0;
  }
};

const formatStorage = (bytes) => {
  if (bytes === undefined) {
   return 'N/A';
  } else if (bytes === 0) {
    return '-';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
};

// This is now handled by fetchFilteredDocuments

// Removed unused getParentFolderName function

// Cache for parent folder names to avoid repeated queries
const parentFolderNameCache = ref({});

// Function to populate the cache for all folders
const populateParentFolderNameCache = async () => {
  try {
    const conn = store.getters.getDBConnection;
    if (!conn) return;
    
    const query = `
      SELECT "Id", "DisplayName" FROM filteredDocuments
      WHERE "IsFolder" = TRUE
    `;
    
    const result = await conn.query(query);
    const rows = result.toArray();
    
    const cache = {};
    rows.forEach(row => {
      cache[row.Id] = row.DisplayName;
    });
    
    // Add root folder
    cache[""] = t("Root");
    
    parentFolderNameCache.value = cache;
  } catch (error) {
    console.error("Error populating parent folder name cache:", error);
  }
};

// Simplified function that uses the cache
const getParentFolderNameFromCache = (parentFolderId) => {
  return parentFolderNameCache.value[parentFolderId] || "Unknown Folder";
};

const handleItemClick = (item) => {
  if (item.isFolder) {
    currentFolderId.value = item.id;
  } else if (item.fileDownloadLink) {
    downloadFile(item);
  }
};

const downloadFile = (item) => {
  if (item.fileDownloadLink) {
    openUrl(item.fileDownloadLink);
  } else {
    console.warn('No download link available for this file.');
  }
};

const goToPath = (item) => {
  currentFolderId.value = item.id;
};

const breadcrumbs = ref([]);

const computeBreadcrumbs = async () => {
  try {
    const conn = store.getters.getDBConnection;
    if (!conn) return;
    
    const breadcrumbItems = [];
    let currentFolderIdLocal = currentFolderId.value;
    
    // If we're not at the root level already
    if (currentFolderIdLocal && currentFolderIdLocal !== "") {
      // Build the breadcrumb path by traversing up through parent folders
      while (currentFolderIdLocal && currentFolderIdLocal !== "") {
        const query = `
          SELECT "Id", "DisplayName", "ParentFolderId", "FullPath" FROM filteredDocuments
          WHERE "Id" = '${currentFolderIdLocal}'
          LIMIT 1
        `;
        
        const result = await conn.query(query);
        const rows = result.toArray();
        
        if (rows.length === 0) break;
        
        const currentFolder = rows[0];
        
        // Add this folder to the breadcrumb path
        breadcrumbItems.unshift({
          title: currentFolder.DisplayName || currentFolder.Id,
          disabled: false,
          href: 'javascript:void(0)',
          id: currentFolder.Id
        });
        
        // If we've reached a folder with FullPath = '/', we've reached the root
        if (currentFolder.FullPath === '/') break;
        
        currentFolderIdLocal = currentFolder.ParentFolderId;
      }
    }
    
    breadcrumbItems.unshift({
      title: t('Root'),
      disabled: currentFolderId.value === "",
      href: 'javascript:void(0)',
      id: rootFolderId.value
    });
    
    if (breadcrumbItems.length > 0) {
      breadcrumbItems[breadcrumbItems.length - 1].disabled = true;
    }
    
    breadcrumbs.value = breadcrumbItems;
  } catch (error) {
    console.error("Error computing breadcrumbs:", error);
    breadcrumbs.value = [{
      title: t('Root'),
      disabled: true,
      href: 'javascript:void(0)',
      id: rootFolderId.value
    }];
  }
};

const getInitials = (name) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getRandomColor = (id) => {
  const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
  const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
};

const formatDate = (dateString, showTime = false) => {
  const date = new Date(dateString);
  return showTime
    ? format(date, 'yyyy-MM-dd HH:mm:ss')
    : format(date, 'yyyy-MM-dd');
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  return formatDistanceToNow(date, { addSuffix: true });
};

const { proxy } = getCurrentInstance();
const openUrl = (url) => {
  if (buildMode.value === 'production') {
    proxy.$launchUrl(url);
  } else {
    window.open(url, '_blank');
  }
};

// Function to refresh all data
const refreshData = async () => {
  await getData();
  await computeCardValues();
  await computeRecentDocumentsCount();
  await populateParentFolderNameCache();
  await fetchFilteredDocuments();
  await fetchLatestDocuments();
  await computeBreadcrumbs();
};

// Watch for changes in search input
watch(search, async () => {
  await fetchFilteredDocuments();
});

// Watch for changes in searchLatest input
watch(searchLatest, async () => {
  await fetchLatestDocuments();
});

// Watch for changes in currentFolderId
watch(currentFolderId, async () => {
  await fetchFilteredDocuments();
  await computeBreadcrumbs();
});

// Watch for changes in activeTab
watch(activeTab, async (newTab) => {
  if (newTab === 'latest') {
    await fetchLatestDocuments();
  } else {
    await fetchFilteredDocuments();
  }
});


onMounted(async () => {
  if (dataLoaded.value) {
    await refreshData();
  } else {
    // Watch for data loaded event
    const unwatch = watch(() => dataLoaded.value, async (newValue) => {
      if (newValue) {
        await refreshData();
        unwatch(); // Remove the watcher once data is loaded
      }
    });
  }
});
</script>

<style scoped>
.v-card {
  height: 100%;
}
.chart {
  height: 400px;
}
</style>